<template>
  <div
    class="tw:rounded-xl tw:flex tw:flex-col tw:p-4 tw:text-gray-800 tw:w-full tw:items-center tw:gap-y-6"
    :class="getClass"
  >
    <div class="tw:text-base tw:w-full tw:font-bold tw:flex tw:items-center tw:text-gray-800">
      <div
        v-if="elementId === 'bookings'"
        class="tw:bg-decorative-lightblue tw:h-[48px] tw:w-[48px] tw:p-3 tw:flex tw:items-center tw:justify-center tw:mr-2 tw:rounded-full"
      >
        <RcBookingsIcon class="tw:size-[18px]" />
      </div>
      <div
        v-if="elementId === 'visits'"
        class="tw:bg-decorative-lightblue tw:p-3 tw:h-[48px] tw:w-[48px] tw:flex tw:items-center tw:justify-center tw:mr-2 tw:rounded-full"
      >
        <RcClicksIcon class="tw:size-[18px]" />
      </div>
      <div
        v-if="elementId === 'revenue'"
        class="tw:bg-decorative-lightblue tw:p-3 tw:h-[48px] tw:w-[48px] tw:flex tw:items-center tw:justify-center tw:mr-2 tw:rounded-full"
      >
        <RcRevenueIcon class="tw:size-[18px]" />
      </div>
      <div
        v-if="elementId === 'cpa'"
        class="tw:bg-decorative-lightblue tw:p-3 tw:h-[48px] tw:w-[48px] tw:flex tw:items-center tw:justify-center tw:mr-2 tw:rounded-full"
      >
        <RcCpaIcon class="tw:size-[18px]" />
      </div>
      <p class="tw:text-left tw:font-bold tw:text-xl">{{ $t(label) }}</p>
      <UiTooltip theme="customdarkwithpadding" tooltip-position="top" :label="getTooltip" class="tw:cursor-pointer">
        <InformationOutline class="tw:fill-none tw:h-[19px] tw:w-[19px] tw:stroke-gray-500 tw:ml-1" />
      </UiTooltip>
    </div>
    <div class="tw:flex tw:w-full tw:justify-start tw:items-center">
      <p class="tw:text-[28px] tw:mr-3">{{ formatNumberValue }}</p>
      <div
        class="tw:gap-x-1 tw:font-bold tw:bg-white tw:flex tw:justify-center tw:items-center tw:border tw:border-gray-300 tw:rounded-md tw:py-1 tw:px-2"
      >
        <ArrowUpRight v-if="isPositive" class="tw:size-[11px] tw:fill-[#0A1121]" />
        <ArrowDownRight v-else class="tw:size-[11px] tw:fill-[#0A1121]" />

        {{ hasNoData || hasEmptyTable ? '-%' : formatPercentage(progression, 0, 0) }}
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'
import RcBookingsIcon from '@/components/icons/RcBookingsIcon.svg?component'
import RcClicksIcon from '@/components/icons/RcClicksIcon.svg?component'
import RcRevenueIcon from '@/components/icons/RcRevenueIcon.svg?component'
import ArrowUpRight from '@/components/icons/ArrowUpRight.svg?component'
import ArrowDownRight from '@/components/icons/ArrowDownRight.svg?component'
import RcCpaIcon from '@/components/icons/RcCpaIcon.svg?component'

import { useFormatting } from '@/composables/useFormatting'

const { t } = useI18n()
const props = defineProps({
  label: {
    type: String,
    required: true,
    default: '',
  },
  elementId: {
    type: String,
    required: false,
    default: '',
  },
  value: {
    type: Number,
    required: false,
    default: null,
  },
  number: {
    type: Number,
    required: false,
    default: null,
  },
  index: {
    type: Number,
    required: true,
    default: 0,
  },
  progression: Number,
  hasNoData: Boolean,
  hasEmptyTable: Boolean,
})

const tooltipTexts = {
  visits: 'rc_def_clicks',
  bookings: 'rc_def_bookings',
  revenue: 'rc_def_revenue',
  cpa: 'rc_def_ecpa',
}

const { formatCurrency, formatPercentage, formatNumber } = useFormatting()
const getTooltip = computed(() => t(tooltipTexts[props.elementId]))
const getNumber = computed(() => props.number ?? 0)
const isPositive = computed(() => props.progression >= 0)
const formatNumberValue = computed(() => {
  if (props.hasEmptyTable || props.hasNoData) return '-'

  if (props.elementId === 'cpa') {
    return formatPercentage(getNumber.value, 0, 0)
  }

  if (props.elementId === 'revenue') {
    return formatCurrency(getNumber.value, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  }

  return formatNumber(getNumber.value, 0, 2)
})

const getClass = computed(() => {
  if (props.isSelected) {
    if (props.index === 0) {
      return 'tw:bg-blue-200 tw:lg:ml-0 '
    } else if (props.index === 3) {
      return 'tw:bg-blue-200 tw:lg:mr-0 '
    } else {
      return 'tw:bg-blue-200 '
    }
  } else {
    if (props.index === 0) {
      return 'tw:bg-gray-100 tw:lg:ml-0'
    } else if (props.index === 3) {
      return 'tw:bg-gray-100 tw:lg:mr-0 '
    } else {
      return 'tw:bg-gray-100 '
    }
  }
})
</script>
